import {BaseInput} from "@/components/BaseInput";
import {CAMPAIGN_CATEGORIES, NOTIFICATION_SCREENS} from "@/constants/push.constants";
import type {CampaignCategory, CampaignPriority, DataPayload} from "@/types/push.types";

const PRIORITY_OPTIONS: {value: CampaignPriority; label: string; description: string}[] = [
  {value: "default", label: "Default", description: "System decides priority"},
  {value: "normal", label: "Normal", description: "Standard delivery"},
  {value: "high", label: "High", description: "Immediate delivery, wakes device"},
];

interface Step1ContentProps {
  formData: {
    title: string;
    body: string;
    category: CampaignCategory;
    priority: CampaignPriority;
    imageUrl?: string;
    actionUrl?: string;
    dataPayload?: DataPayload;
  };
  onChange: (data: Step1ContentProps["formData"]) => void;
  errors: Record<string, string>;
}

export const Step1Content = ({formData, onChange, errors}: Step1ContentProps) => {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2
          className="text-lg font-semibold mb-1"
          style={{color: "var(--color-text)"}}
        >
          Step 1: Content
        </h2>
        <p
          className="text-sm"
          style={{color: "var(--color-text-secondary)"}}
        >
          Define the notification title, body, and category
        </p>
      </div>

      {/* Title */}
      <div>
        <label
          htmlFor="title"
          className="block text-sm font-medium mb-1.5"
          style={{color: "var(--color-text)"}}
        >
          Title <span style={{color: "var(--color-danger)"}}>*</span>
        </label>
        <BaseInput
          id="title"
          type="text"
          placeholder="Enter notification title"
          value={formData.title}
          onChange={e => onChange({...formData, title: e.target.value})}
          error={errors.title}
        />
        <p
          className="text-xs mt-1"
          style={{color: "var(--color-text-secondary)"}}
        >
          {formData.title.length}/200 characters
        </p>
      </div>

      {/* Body */}
      <div>
        <label
          htmlFor="body"
          className="block text-sm font-medium mb-1.5"
          style={{color: "var(--color-text)"}}
        >
          Body <span style={{color: "var(--color-danger)"}}>*</span>
        </label>
        <textarea
          id="body"
          rows={5}
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={{
            backgroundColor: "var(--color-bg)",
            border: errors.body ? "1px solid var(--color-danger)" : "1px solid var(--color-surface-2)",
            color: "var(--color-text)",
          }}
          placeholder="Enter notification body text"
          value={formData.body}
          onChange={e => onChange({...formData, body: e.target.value})}
        />
        <p
          className="text-xs mt-1"
          style={{color: "var(--color-text-secondary)"}}
        >
          {formData.body.length}/1000 characters
        </p>
      </div>

      {/* Category */}
      <div>
        <label
          htmlFor="category"
          className="block text-sm font-medium mb-1.5"
          style={{color: "var(--color-text)"}}
        >
          Category <span style={{color: "var(--color-danger)"}}>*</span>
        </label>
        <select
          id="category"
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={{
            backgroundColor: "var(--color-bg)",
            border: "1px solid var(--color-surface-2)",
            color: "var(--color-text)",
          }}
          value={formData.category}
          onChange={e => onChange({...formData, category: e.target.value as CampaignCategory})}
        >
          {CAMPAIGN_CATEGORIES.map(cat => (
            <option
              key={cat.value}
              value={cat.value}
            >
              {cat.label} — {cat.description}
            </option>
          ))}
        </select>
      </div>

      {/* Priority */}
      <div>
        <label
          htmlFor="priority"
          className="block text-sm font-medium mb-1.5"
          style={{color: "var(--color-text)"}}
        >
          Priority
        </label>
        <select
          id="priority"
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={{
            backgroundColor: "var(--color-bg)",
            border: "1px solid var(--color-surface-2)",
            color: "var(--color-text)",
          }}
          value={formData.priority}
          onChange={e => onChange({...formData, priority: e.target.value as CampaignPriority})}
        >
          {PRIORITY_OPTIONS.map(opt => (
            <option
              key={opt.value}
              value={opt.value}
            >
              {opt.label} — {opt.description}
            </option>
          ))}
        </select>
      </div>

      {/* Image URL */}
      <div>
        <label
          htmlFor="image-url"
          className="block text-sm font-medium mb-1.5"
          style={{color: "var(--color-text)"}}
        >
          Image URL (Optional)
        </label>
        <BaseInput
          id="image-url"
          type="url"
          placeholder="https://example.com/image.jpg"
          value={formData.imageUrl ?? ""}
          onChange={e => onChange({...formData, imageUrl: e.target.value || undefined})}
        />
        <p
          className="text-xs mt-1"
          style={{color: "var(--color-text-secondary)"}}
        >
          Rich notifications can include an image
        </p>
      </div>

      {/* Action URL */}
      <div>
        <label
          htmlFor="action-url"
          className="block text-sm font-medium mb-1.5"
          style={{color: "var(--color-text)"}}
        >
          Action URL (Optional)
        </label>
        <BaseInput
          id="action-url"
          type="url"
          placeholder="https://example.com/promo"
          value={formData.actionUrl ?? ""}
          onChange={e => onChange({...formData, actionUrl: e.target.value || undefined})}
        />
        <p
          className="text-xs mt-1"
          style={{color: "var(--color-text-secondary)"}}
        >
          Deep link or web URL to open when the user taps the notification
        </p>
      </div>

      {/* Navigation Screen */}
      <div>
        <label
          htmlFor="screen"
          className="block text-sm font-medium mb-1.5"
          style={{color: "var(--color-text)"}}
        >
          Navigation Screen (Optional)
        </label>
        <select
          id="screen"
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={{
            backgroundColor: "var(--color-bg)",
            border: "1px solid var(--color-surface-2)",
            color: "var(--color-text)",
          }}
          value={formData.dataPayload?.screen ?? ""}
          onChange={e =>
            onChange({
              ...formData,
              dataPayload: {...formData.dataPayload, screen: e.target.value || undefined},
            })
          }
        >
          <option value="">None (Notification Inbox)</option>
          {NOTIFICATION_SCREENS.map(screen => (
            <option
              key={screen.value}
              value={screen.value}
            >
              {screen.label}
            </option>
          ))}
        </select>
        <p
          className="text-xs mt-1"
          style={{color: "var(--color-text-secondary)"}}
        >
          Where should the user be redirected when tapping the notification?
        </p>
      </div>

      {/* Preview */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{color: "var(--color-text)"}}
        >
          Preview
        </label>
        <div
          className="p-4 rounded-lg border"
          style={{
            backgroundColor: "var(--color-surface-2)",
            borderColor: "var(--color-surface-2)",
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{backgroundColor: "var(--color-primary)", color: "white"}}
            >
              <span className="text-sm font-bold">R365</span>
            </div>
            <div className="flex-1">
              <p
                className="text-sm font-semibold mb-1"
                style={{color: "var(--color-text)"}}
              >
                {formData.title || "Notification title"}
              </p>
              <p
                className="text-xs"
                style={{color: "var(--color-text-secondary)"}}
              >
                {formData.body || "Notification body text will appear here"}
              </p>
              {formData.imageUrl && (
                <div className="mt-2">
                  <img
                    src={formData.imageUrl}
                    alt="Preview"
                    className="w-full rounded max-h-32 object-cover"
                    onError={e => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
